import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { PersonTable } from './PersonTable';
import { Person } from '@/types/table.ts';
import { useTranslation } from '@/hooks/useTranslation.ts';

const generateMockData = (): Person[] => [
  {
    id: '1',
    firstName: 'Max',
    lastName: 'Müller',
    email: 'max.mueller@example.de',
    age: 30,
    city: 'Berlin',
    state: 'BE',
    salary: 70000,
    department: 'Entwicklung',
    startDate: '2022-02-10',
    isActive: true,
  },
  {
    id: '2',
    firstName: 'Sophie',
    lastName: 'Schneider',
    email: 'sophie.schneider@example.de',
    age: 27,
    city: 'München',
    state: 'BY',
    salary: 75000,
    department: 'Design',
    startDate: '2021-06-15',
    isActive: true,
  },
  {
    id: '3',
    firstName: 'Lukas',
    lastName: 'Weber',
    email: 'lukas.weber@example.de',
    age: 40,
    city: 'Hamburg',
    state: 'HH',
    salary: 68000,
    department: 'Marketing',
    startDate: '2020-09-01',
    isActive: false,
  },
  {
    id: '4',
    firstName: 'Anna',
    lastName: 'Fischer',
    email: 'anna.fischer@example.de',
    age: 35,
    city: 'Frankfurt',
    state: 'HE',
    salary: 80000,
    department: 'Entwicklung',
    startDate: '2022-11-20',
    isActive: true,
  },
  {
    id: '5',
    firstName: 'Felix',
    lastName: 'Hoffmann',
    email: 'felix.hoffmann@example.de',
    age: 38,
    city: 'Köln',
    state: 'NW',
    salary: 82000,
    department: 'Management',
    startDate: '2019-12-05',
    isActive: true,
  },
  {
    id: '6',
    firstName: 'Laura',
    lastName: 'Wagner',
    email: 'laura.wagner@example.de',
    age: 29,
    city: 'Stuttgart',
    state: 'BW',
    salary: 71000,
    department: 'HR',
    startDate: '2023-03-12',
    isActive: true,
  },
  {
    id: '7',
    firstName: 'Jonas',
    lastName: 'Schmidt',
    email: 'jonas.schmidt@example.de',
    age: 42,
    city: 'Düsseldorf',
    state: 'NW',
    salary: 79000,
    department: 'Analyse',
    startDate: '2021-08-18',
    isActive: false,
  },
  {
    id: '8',
    firstName: 'Mia',
    lastName: 'Neumann',
    email: 'mia.neumann@example.de',
    age: 33,
    city: 'Leipzig',
    state: 'SN',
    salary: 76000,
    department: 'Vertrieb',
    startDate: '2022-10-25',
    isActive: true,
  },
];

export const TableExample = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<Person[]>(generateMockData());
  const [loading, setLoading] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [_editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleEdit = (person: Person) => {
    setSelectedPerson(person);
    setEditDialogOpen(true);
  };

  const handleDelete = (person: Person) => {
    setSelectedPerson(person);
    setDeleteDialogOpen(true);
  };

  const handleAddNew = () => {
    const newPerson: Person = {
      id: (data.length + 1).toString(),
      firstName: 'Новый',
      lastName: 'Сотрудник',
      email: 'new@example.com',
      age: 25,
      city: 'Москва',
      state: 'МО',
      salary: 50000,
      department: 'Разработка',
      startDate: new Date().toISOString().split('T')[0],
      isActive: true,
    };
    setData(prev => [...prev, newPerson]);
    showSnackbar(t('table.messages.added'));
  };

  const handleRefresh = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setData(generateMockData());
    setLoading(false);
    showSnackbar(t('table.messages.refreshed'));
  };

  const handleConfirmDelete = () => {
    if (selectedPerson) {
      setData(prev => prev.filter(p => p.id !== selectedPerson.id));
      showSnackbar(t('table.messages.deleted'));
    }
    setDeleteDialogOpen(false);
    setSelectedPerson(null);
  };

  const stats = useMemo(() => {
    const total = data.length;
    const active = data.filter(p => p.isActive).length;
    const inactive = total - active;
    const avgSalary = data.reduce((sum, p) => sum + p.salary, 0) / total;
    const avgAge = data.reduce((sum, p) => sum + p.age, 0) / total;

    return { total, active, inactive, avgSalary, avgAge };
  }, [data]);

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('table.title')}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        {t('table.description')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="primary">
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('table.stats.total')}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="success.main">
              {stats.active}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('table.stats.active')}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="error.main">
              {stats.inactive}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('table.stats.inactive')}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" color="info.main">
              {Math.round(stats.avgAge)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('table.stats.avgAge')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNew}
        >
          {t('table.actions.add')}
        </Button>
        <Button variant="outlined" onClick={handleRefresh} disabled={loading}>
          {t('table.actions.refresh')}
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <PersonTable
          data={data}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />
      </Paper>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t('table.dialogs.delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('table.dialogs.delete.message', {
              name: selectedPerson
                ? `${selectedPerson.firstName} ${selectedPerson.lastName}`
                : '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            {t('table.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
